import { ButtonHTMLAttributes, FC, HTMLAttributes, useCallback, useMemo } from 'react';
import classNames from 'classnames';

import EditButton from '@z-components/atoms/EditButton';
import {
  logEvent,
  makeGARequestIdLabel,
  getRequestDashboardCategory,
} from '@z-components/utils/google-analytics';

import styles from './ProProfileIntroduction.module.scss';

const TRANSLATION = {
  en: {
    aboutPro: 'About Me',
    editLabel: 'Edit',
    introductionTitles: {
      description: 'Company information・Self-introduction',
      interest: 'Passion・Service goal',
      skills: 'Work experience・Skills',
      title: 'Headline',
    },
  },
  ja: {
    aboutPro: '自己紹介',
    editLabel: '編集',
    introductionTitles: {
      description: '企業情報・自己紹介',
      interest: 'お仕事で心がけていること',
      skills: '経歴・資格',
      title: '一言PR',
    },
  },
};

const FIELDS = ['description', 'skills', 'interest'] as ['description', 'skills', 'interest'];

interface Props {
  isProView: boolean;
  language: 'en' | 'ja';
  markEngagementProWebsiteUrlClick?: () => void;
  onEditClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
  onWebsiteUrlClick?: HTMLAttributes<HTMLAnchorElement>['onClick'];
  profile: {
    description?: string;
    interest?: string;
    skills?: string;
    title?: string;
    website?: string;
  };
  requestId?: string;
}

const ProProfileIntroduction: FC<Props> = ({
  language = 'ja',
  onEditClick,
  onWebsiteUrlClick,
  markEngagementProWebsiteUrlClick,
  profile = { description: '', interest: '', skills: '', title: '', website: '' },
  isProView = false,
  requestId,
}) => {
  const gaCategory = useMemo(() => (isProView ? '' : getRequestDashboardCategory('client')), [
    isProView,
  ]);
  const handleWebsiteUrlClick = useCallback(
    (event) => {
      logEvent(gaCategory, 'proWebsiteClicked', makeGARequestIdLabel(requestId));
      if (!isProView && markEngagementProWebsiteUrlClick) {
        markEngagementProWebsiteUrlClick();
      }
      if (onWebsiteUrlClick) {
        onWebsiteUrlClick(event);
      }
    },
    [gaCategory, requestId, isProView, markEngagementProWebsiteUrlClick, onWebsiteUrlClick]
  );

  return (
    <section>
      <div className={styles['header-container']}>
        {isProView && (
          <>
            <h3
              className={classNames(styles.heading, {
                [styles['heading-editable']]: onEditClick,
              })}
            >
              {TRANSLATION[language].aboutPro}
            </h3>
            {
              <EditButton
                onClick={onEditClick}
                label={TRANSLATION[language].editLabel}
                testId="pro-profile-introduction-edit"
                size={18}
              />
            }
          </>
        )}
      </div>

      {profile.website && (
        <p className={styles['website-info']}>
          <a
            href={profile.website}
            rel="noreferrer noopener"
            target="_blank"
            onClick={handleWebsiteUrlClick}
          >
            <img alt="Website" height={24} src="/static/images/internet_icon.svg" width={24} />
            <span className={styles['website-link']}>{profile.website}</span>
          </a>
        </p>
      )}

      {profile.title && (
        <div key="title" className={styles['pr-content-container']}>
          <h3 className={styles.title}>{TRANSLATION[language].introductionTitles.title}</h3>
          <p className={styles.content}>{profile.title}</p>
        </div>
      )}

      {FIELDS.map((field) =>
        profile[field] ? (
          <div key={field} className={styles.field}>
            <h3 className={styles.title}>{TRANSLATION[language].introductionTitles[field]}</h3>
            <p className={styles.content}>{profile[field]}</p>
          </div>
        ) : null
      )}
    </section>
  );
};

export default ProProfileIntroduction;
export type { Props as ProProfileIntroductionProps };
